import { useState } from 'react'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Button, Stack, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, Alert, Tooltip, Autocomplete, Checkbox,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Download, PlayArrow, Delete, Schedule, Add, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import {
  useReportTemplates, useReportSchedules, useReportFiles,
  useGenerateReport, useCreateSchedule, useDeleteSchedule,
  downloadReportFile, ReportTemplate,
} from '../../hooks/useReports'
import { useDevices, Device, VEHICLE_TYPES } from '../../hooks/useDevices'
import ReportBuilderDialog from './ReportBuilderDialog'

function unwrapDevices(d: unknown): Device[] {
  if (Array.isArray(d)) return d
  const o = d as { devices?: Device[] } | undefined
  return o?.devices ?? []
}

const SCHEDULES = ['once', 'daily', 'weekly', 'monthly'] as const
const DATE_RANGES = [
  { value: 1, label: 'Last 1 day' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
]

export default function ReportsPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const [builderOpen, setBuilderOpen] = useState(false)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} />
        <Button variant="contained" onClick={() => setBuilderOpen(true)}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
          Custom Report Builder
        </Button>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t('reports.templates')} />
        <Tab label={t('reports.schedules')} />
        <Tab label={t('reports.history')} />
      </Tabs>
      {tab === 0 && <TemplatesTab />}
      {tab === 1 && <SchedulesTab />}
      {tab === 2 && <HistoryTab />}
      <ReportBuilderDialog open={builderOpen} onClose={() => setBuilderOpen(false)} />
    </Box>
  )
}

function TemplatesTab() {
  const { t } = useTranslation()
  const { data: templates = [], isLoading } = useReportTemplates()
  const generate = useGenerateReport()
  const [generating, setGenerating] = useState<string | null>(null)

  const handleGenerate = async (tmpl: ReportTemplate) => {
    setGenerating(tmpl.id)
    try {
      await generate.mutateAsync({
        report_type: tmpl.report_type,
        config: tmpl.default_config,
        name: tmpl.name,
      })
      analytics.track(EVENTS.REPORT_GENERATED, { report_type: tmpl.report_type, report_name: tmpl.name })
    } finally {
      setGenerating(null)
    }
  }

  if (isLoading) return <CircularProgress />

  return (
    <Stack spacing={2}>
      {templates.length === 0 && <Alert severity="info">{t('reports.noTemplates')}</Alert>}
      {templates.map((tmpl) => (
        <Card key={tmpl.id} sx={{ border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body1" fontWeight={700}>{tmpl.name}</Typography>
              <Typography variant="body2" color="text.secondary">{tmpl.description}</Typography>
              <Chip label={tmpl.report_type} size="small" variant="outlined" sx={{ mt: 0.5 }} />
            </Box>
            <Button
              variant="contained" startIcon={generating === tmpl.id ? <CircularProgress size={16} /> : <PlayArrow />}
              onClick={() => handleGenerate(tmpl)}
              disabled={generating === tmpl.id}
              sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', whiteSpace: 'nowrap' }}
            >
              {t('reports.generateNow')}
            </Button>
          </CardContent>
        </Card>
      ))}
      {generate.isSuccess && (
        <Alert severity="success">
          {t('reports.generated')} — {t('reports.checkHistory')}
        </Alert>
      )}
    </Stack>
  )
}

function SchedulesTab() {
  const { t } = useTranslation()
  const { data: schedules = [], isLoading } = useReportSchedules()
  const { data: templates = [] } = useReportTemplates()
  const { data: devicesRaw } = useDevices()
  const devices = unwrapDevices(devicesRaw)
  const createSchedule = useCreateSchedule()
  const deleteSchedule = useDeleteSchedule()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    name: '', report_type: '', schedule: 'daily' as string,
    email_to: '', date_range_days: 7,
    device_ids: [] as string[], vehicle_types: [] as string[],
  })

  const handleCreate = async () => {
    await createSchedule.mutateAsync({
      name: form.name,
      report_type: form.report_type,
      schedule: form.schedule,
      email_to: form.email_to,
      config: {
        date_range_days: form.date_range_days,
        device_ids: form.device_ids.length > 0 ? form.device_ids : undefined,
        vehicle_types: form.vehicle_types.length > 0 ? form.vehicle_types : undefined,
      },
    } as any)
    setOpen(false)
  }

  const cols: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1 },
    { field: 'report_type', headerName: t('common.type'), width: 150 },
    { field: 'schedule', headerName: t('reports.frequency'), width: 120,
      renderCell: ({ value }) => <Chip label={value} size="small" /> },
    { field: 'email_to', headerName: t('reports.emailTo'), flex: 1 },
    { field: 'enabled', headerName: t('common.status'), width: 100,
      renderCell: ({ value }) => <Chip label={value ? t('common.active') : t('common.inactive')} size="small" color={value ? 'success' : 'default'} /> },
    { field: 'next_run_at', headerName: t('reports.nextRun'), width: 160,
      valueFormatter: (v: string) => v ? format(new Date(v), 'PPp') : '—' },
    { field: 'actions', headerName: '', width: 60, sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={t('common.delete')}>
          <IconButton size="small" onClick={() => deleteSchedule.mutate(row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      ) },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
          {t('reports.createSchedule')}
        </Button>
      </Box>
      <DataGrid rows={schedules} columns={cols} loading={isLoading} autoHeight
        pageSizeOptions={[10, 25]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('reports.createSchedule')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label={t('common.name')} required value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <TextField select label={t('reports.reportType')} value={form.report_type}
            onChange={(e) => setForm((p) => ({ ...p, report_type: e.target.value }))}>
            {templates.map((t) => <MenuItem key={t.report_type} value={t.report_type}>{t.name}</MenuItem>)}
          </TextField>
          <TextField select label={t('reports.frequency')} value={form.schedule}
            onChange={(e) => setForm((p) => ({ ...p, schedule: e.target.value }))}>
            {SCHEDULES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField select label={t('reports.dateRange')} value={form.date_range_days}
            onChange={(e) => setForm((p) => ({ ...p, date_range_days: Number(e.target.value) }))}>
            {DATE_RANGES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
          <Autocomplete multiple size="small" options={devices}
            getOptionLabel={(d) => d.name} value={devices.filter((d) => form.device_ids.includes(d.id))}
            onChange={(_, v) => setForm((p) => ({ ...p, device_ids: v.map((d) => d.id) }))}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />} checked={selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={t('reports.filterDevices')} />}
          />
          <Autocomplete multiple size="small" options={[...VEHICLE_TYPES]}
            value={form.vehicle_types}
            onChange={(_, v) => setForm((p) => ({ ...p, vehicle_types: v }))}
            renderInput={(params) => <TextField {...params} label={t('reports.filterVehicleTypes')} />}
          />
          <TextField label={t('reports.emailTo')} value={form.email_to}
            onChange={(e) => setForm((p) => ({ ...p, email_to: e.target.value }))}
            placeholder="user@gmail.com, manager@company.com"
            helperText={t('reports.emailHelperText')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={!form.name || !form.report_type || createSchedule.isPending}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function HistoryTab() {
  const { t } = useTranslation()
  const { data: files = [], isLoading } = useReportFiles()

  const cols: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1 },
    { field: 'report_type', headerName: t('common.type'), width: 150 },
    { field: 'file_size', headerName: t('reports.fileSize'), width: 120,
      valueFormatter: (v: number) => v ? `${(v / 1024).toFixed(1)} KB` : '—' },
    { field: 'created_at', headerName: t('reports.generatedAt'), width: 180,
      valueFormatter: (v: string) => v ? format(new Date(v), 'PPp') : '—' },
    { field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={t('reports.download')}>
          <IconButton size="small" onClick={() => { downloadReportFile(row.id) }} color="primary">
            <Download fontSize="small" />
          </IconButton>
        </Tooltip>
      ) },
  ]

  return (
    <DataGrid rows={files} columns={cols} loading={isLoading} autoHeight
      pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      disableRowSelectionOnClick sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
  )
}
